import { useEffect, useRef, useState } from "react";
import { Navigation, NavigationOff } from "lucide-react";
import useAuthStore from "../store/authStore";

export default function AgentGPSTracker({ sendLocation }) {
  const { user } = useAuthStore();
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);
  const watchRef = useRef(null);

  useEffect(() => {
    if (user?.role !== "agent") return;
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }

    // Start watching position
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setSharing(true);
        setError(null);
        sendLocation(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        setSharing(false);
        if (err.code === 1) setError("Location access denied");
        else setError("Location unavailable");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => {
      if (watchRef.current !== null) {
        navigator.geolocation.clearWatch(watchRef.current);
      }
    };
  }, [user?.role, sendLocation]);

  if (user?.role !== "agent") return null;

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${
        sharing
          ? "bg-green-50 text-green-700 border border-green-200"
          : error
          ? "bg-red-50 text-red-600 border border-red-200"
          : "bg-slate-50 text-slate-400 border border-slate-200"
      }`}
      data-testid="gps-tracker-status"
      title={error || (sharing ? "Sharing live GPS with customers" : "Waiting for GPS...")}
    >
      {sharing ? (
        <>
          <Navigation className="h-3 w-3" />
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          GPS Live
        </>
      ) : (
        <>
          <NavigationOff className="h-3 w-3" />
          {error || "GPS Off"}
        </>
      )}
    </div>
  );
}
